import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { HIGHLIGHT_MESSAGE_UNAVAILABLE_FOR_DATES } from 'components/OfferList/OfferListTiles/constants'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { getOfferWithDiscountKey } from 'actions/MarketingAction'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTile from '../SearchTile'
import SearchTileHighlightBanner from '../shared/SearchTileHighlightBanner'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import BedbankSearchTileAction from './BedbankSearchTileAction'
import BedbankSearchTilePriceStack from './BedbankSearchTilePriceStack'
import BedbankSearchTileUnavailabilityBanner from './BedbankSearchTileUnavailabilityBanner'
import ExperimentalBedbankLTSSearchTileFeatures from './ExperimentalBedbankLTSSearchTileFeatures'
import ExperimentalBedbankSearchTileFeatures from './ExperimentalBedbankSearchTileFeatures'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import SearchTileComparisonCheckbox from '../shared/SearchTileComparisonCheckbox'
import { getLoyaltyBedbankPackageId } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import useOfferRating from 'hooks/Offers/useOfferRating'
import * as Analytics from 'analytics/analytics'
import { generalLog } from 'marketing/api'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import BedbankLTSSearchTileFeatures from './BedbankLTSSearchTileFeatures'
import BedbankSearchTileFeatures from './BedbankSearchTileFeatures'
import useMetaSearchDistance from './useMetaSearchDistance'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import CurrencyContext from 'contexts/currencyContext'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import GeoContext from 'contexts/geoContext'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import SalesBlock, { MAX_DISPLAY_RATES } from 'LuxKit/PricePoints/SalesBlock'
import { sortBy } from 'lib/array/arrayUtils'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  showDescription?: boolean
  position?: number
}

function BedbankSearchTile({ offer, filters, showDescription = false, position }: Props) {
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)
  const locations = useMemo<Array<string>>(() => {
    if (offer.property.locationTitleOverride) return [offer.property.locationTitleOverride]

    return [
      [offer.property.address.city, offer.property.address.countryName].filter(Boolean).join(', '),
    ]
  }, [offer])

  const mapUrl = useMapSearchOfferUrl(false, offer, filters)

  const utmBp = useAppSelector(state => state.utm.bp)

  const shouldUseSearchPrices = getShouldUseSearchPrices(filters)

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights
  const promotions = useBedbankRoomRatePromotions(offer.promotions, filters?.checkIn, filters?.checkOut, isAnytimeSearch)
  const hasPromotions = offerMetaData?.hasPromotions || !!Object.values(promotions).length

  const discountKey = getOfferWithDiscountKey(offer.id, filters?.checkIn, filters?.checkOut)
  const offerWithDiscount = useAppSelector(state => state.offer.offersWithDiscount[discountKey])
  const discountedPackages = useMemo(() => {
    if (!offerWithDiscount?.discountedPackages) return undefined
    return sortBy(offerWithDiscount.discountedPackages, (pkg) => pkg.discountPercentage, 'desc')
  }, [offerWithDiscount?.discountedPackages])

  const shouldForceFetchRate = !!(filters?.checkIn && filters?.checkOut)
  const {
    offerPrice,
    offerLoyaltyPrice,
    offerLoyaltyMemberPrice,
    shownRate,
    fetchingRates,
    mx,
  } = useBedbankOfferSearchPricing(offer, filters, undefined, shouldForceFetchRate)
  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer.property.timezone])

  const hasDates = !!(filters?.checkIn && filters.checkOut)
  const isSoldOut = shouldUseSearchPrices ? !offerMetaData?.available : (!shownRate && hasDates && !fetchingRates)

  const highlightMessage = useMemo<string | undefined>(() => {
    if (
      filters?.propertyId &&
      !filters?.searchNearby &&
      offer.property?.id === filters.propertyId.split(':')[1]
    ) { // This is a property search result
      if (filters?.checkIn && filters?.checkOut) {
        if (isSoldOut) {
          return HIGHLIGHT_MESSAGE_UNAVAILABLE_FOR_DATES(offer.saleUnit)
        }
        return 'We found a great price for your hotel. Book now to secure this rate!'
      } else {
        return 'We found the property you were interested in. Select your dates to book now.'
      }
    }
  }, [filters, offer, isSoldOut])

  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (fetchingRates) {
      return []
    }
    return [{
      price: offerLoyaltyPrice,
      memberPrice: offerLoyaltyMemberPrice,
      mx,
      loyaltyProductType: 'hotel_lpp_eps',
      packageId: getLoyaltyBedbankPackageId(offer.property.id, shownRate?.supplierCode),
      offerId: offer.id,
      earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode: offer.property.address.countryCode, currentRegionCode } })
    }]
  },
  [currentRegionCode, fetchingRates, mx, offer.id, offer.property.address.countryCode, offer.property.id, offerLoyaltyMemberPrice, offerLoyaltyPrice, shownRate?.supplierCode])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        availability: false,
        roomRateId: undefined,
        extendedAvailability: offerMetaData?.extendedAvailability,
        price: undefined,
        memberPrice: undefined,
        duration: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  useEffect(() => {
    if (highlightMessage && offerPrice && fullPath.includes('metasearch')) {
      const priceTotal = queryParams.priceTotal || 0
      generalLog({
        status: 'info',
        message: 'GoogleHotelSearchPage: highlightedPackage',
        payload: { ...queryParams, fullPath, priceReceived: offerPrice, priceDifference: Number(priceTotal) - offerPrice },
      })
    }
  }, [highlightMessage, offerPrice, queryParams, fullPath])

  const hidePricing = useHideAnytimePrices(offer.type, offerMetaData?.hidePricing)
  const rating = useOfferRating(offer, offerMetaData, fullPath.includes('metasearch'))

  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  const handleSelectDatesClick = useCallback(() => {
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])
  const metaSearchDistanceToMainOffer = useMetaSearchDistance({ offer, filters })

  const trackedSalesBlockImpressionOfferIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!discountedPackages?.length) {
      return
    }

    if (trackedSalesBlockImpressionOfferIdRef.current === offer.id) {
      return
    }
    const packagesToTrack = discountedPackages.slice(0, MAX_DISPLAY_RATES)

    const itemContexts = packagesToTrack.reduce<Array<ReturnType<typeof comluxgroup.createItem_1_1_1>>>((acc, pkg, index) => {
      const salePriceRaw = Number(pkg.salePrice)
      const originalPriceRaw = Number(pkg.price)
      const salePrice = Number.isFinite(salePriceRaw) ? salePriceRaw : Number.isFinite(originalPriceRaw) ? originalPriceRaw : undefined
      const originalPrice = Number.isFinite(originalPriceRaw) ? originalPriceRaw : undefined

      if (salePrice === undefined || originalPrice === undefined) {
        return acc
      }

      const itemCurrency = (pkg as typeof pkg & { currency?: string }).currency ?? currency

      if (!itemCurrency) {
        return acc
      }

      acc.push(comluxgroup.createItem_1_1_1({
        offerId: offer.id,
        categoryId: 'hotel',
        productId: 'bedbank',
        packageId: pkg.rateId,
        currency: itemCurrency,
        price: salePrice,
        value: originalPrice,
        metadata: JSON.stringify({
          pinnedOffer: true,
        }),
        position: index + 1,
      }))

      return acc
    }, [])

    if (!itemContexts.length) {
      return
    }

    trackedSalesBlockImpressionOfferIdRef.current = offer.id

    Analytics.trackEvent({
      context: [
        ...itemContexts,
      ],
      event: comluxgroup.createProductImpression_1_0_3({}),
      options: {
        contextsToSkip: ['item'],
      },
    })
  }, [currency, offer.id, discountedPackages])

  return <SearchTile
    state={highlightMessage ? 'highlighted' : 'default'}
    action={
      !isSoldOut &&
      (
        <BedbankSearchTileAction
          offer={offer}
          filters={filters}
          utmBp={utmBp}
          onSelectDatesClick={handleSelectDatesClick}
          hidePricing={hidePricing}
        />
      )
    }
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
          position={position}
        />
      )}
      {!!highlightMessage && <SearchTileHighlightBanner description={highlightMessage} />}
    </>}
    bookmarkAction={<BookmarkButton offer={offer} />}
    cancellationPolicyLabel={(!noGoogleAffiliations && offer.productType === 'limited_time_special' && (shownRate || cancellationPolicy)) && <BedbankCancellationPolicy
      rate={shownRate ?? cancellationPolicy}
      showNonCancellation
    />}
    bottomExtension={isSoldOut &&
      <BedbankSearchTileUnavailabilityBanner
        offer={offer}
        unavailableReason={offerMetaData?.unavailableReason}
        filters={filters}
      />}
    features={<>
      {discountedPackages && <SalesBlock label="Limited time sale on select rooms" rates={discountedPackages} />}
      {!hasPromotions && (
        <>
          {!noGoogleAffiliations && <BedbankSearchTileFeatures offer={offer} filters={filters} />}
          {noGoogleAffiliations && <ExperimentalBedbankSearchTileFeatures offer={offer} filters={filters} />}
        </>
      )}
      {hasPromotions && (
        <>
          {!noGoogleAffiliations && <BedbankLTSSearchTileFeatures offer={offer} />}
          {noGoogleAffiliations && <ExperimentalBedbankLTSSearchTileFeatures offer={offer} filters={filters} />}
        </>
      )}
    </>}
    meta={<LoyaltyPointsEarn size="M" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />}
    images={offer.images}
    overline={<SearchTileRowOverlineLocation locations={locations} mapUrl={mapUrl} />}
    pricePoint={!isSoldOut && <BedbankSearchTilePriceStack offer={offer} filters={filters} hidePrice={hidePricing} />}
    labels={<ContentTagLabels offerId={offer.id} page="search" />}
    productLabel={<LabelGroup>
      {hasPromotions && <ProductTypeLabel productType={offer.productType} />}
      {metaSearchDistanceToMainOffer && <PromotionLabel variant="default">{metaSearchDistanceToMainOffer}</PromotionLabel>}
    </LabelGroup>}
    rating={rating}
    title={offer.name}
    comparisonCheckbox={<SearchTileComparisonCheckbox offerType={offer.type} offerId={offer.id} name={offer.name} filters={filters} />}
    offer={offer}
  />
}

export default BedbankSearchTile
